import clsx from 'clsx'
import React, { CSSProperties, FC } from 'react'

import { Product, ProductsAndCaption } from '@Components/CMS/Image/ProductsAndCaption'
import { PictureV2, PictureV2Props } from '@Components/Image'
import { Link } from '@Components/Link'
import { PageType } from '@Generated/graphql'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { Image as ImageData } from '@Globals/Image'

import styles from './Image.module.scss'

interface LinkProps {
  destPageType?: string
  destPageTypeId?: string
  link?: string
  linkNewTab?: boolean
}

type SizeType = 'column' | 'fillContainer' | 'fullScreen' | 'fullWidth'

interface ImageProps extends LinkProps {
  caption?: string
  className?: string
  crop?: string
  desktopImage: ImageData
  desktopMaxHeight?: number
  id?: string
  mobileImage?: ImageData
  overlay?: boolean
  priority?: boolean
  products?: Product[]
  type: SizeType
}

interface ImageWrapLinkProps extends LinkProps {
  children?: React.ReactNode
  style?: object
}

const ImageWrapLink: FC<ImageWrapLinkProps> = ({
  children,
  destPageType,
  destPageTypeId,
  link,
  linkNewTab,
  style,
}) => {
  const open = useCMSModalOpen()

  if (destPageType === 'modals' && destPageTypeId) {
    return (
      <div
        className={clsx(styles.imageWrap, styles.modalOpener)}
        onClick={() => open(PageType.Modal, destPageTypeId)}
        style={style}
      >
        {children}
      </div>
    )
  }

  return (
    <Link
      analytics={{
        dest_page_type: destPageType || '',
        dest_page_type_id: destPageTypeId || '',
        source_id: 'image_module',
      }}
      className={styles.imageWrap}
      href={link}
      rel="noreferrer"
      style={style}
      target={linkNewTab ? '_blank' : undefined}
    >
      {children}
    </Link>
  )
}

const breakpoints: Record<string, PictureV2Props['breakpoints']> = {
  column: [
    [320, '100vw'],
    [480, '100vw'],
    [768, 750],
    [1000, 750],
  ],
  // TODO: maybe consider checking the size of the grid to determine this?
  fillContainer: [
    [320, '100vw'],
    [480, '100vw'],
    [768, '100vw'],
    [800, '100vw'],
    [1000, '100vw'],
    [1200, 1200],
    [1920, 1200],
  ],
  fullScreen: [
    [320, '100vw'],
    [480, '100vw'],
    [768, '100vw'],
    [800, '100vw'],
    [1000, '100vw'],
    [1200, '100vw'],
    [1920, '100vw'],
  ],
  fullWidth: [
    [320, '100vw'],
    [480, '100vw'],
    [768, '100vw'],
    [800, '100vw'],
    [1000, '100vw'],
    [1200, 1200],
    [1920, 1200],
  ],
}
export const Image: FC<ImageProps> = ({
  caption,
  crop = 'Top',
  className,
  desktopMaxHeight,
  id,
  desktopImage,
  mobileImage,
  overlay,
  priority,
  products = [],
  type,
  link,
  ...props
}) => {
  const picture = (
    <PictureV2
      breakpoints={breakpoints[type]}
      cropRatio={type === 'fullScreen' && desktopMaxHeight ? `100:${desktopMaxHeight}` : undefined}
      desktop={desktopImage}
      loading={priority ? 'eager' : 'lazy'}
      mobile={mobileImage}
      objectFit="cover"
      objectPosition={`center ${crop.toLowerCase()}`}
      priority={priority}
    />
  )

  return (
    <div
      className={clsx(
        className,
        styles.image,
        type === 'column' && styles.column,
        type === 'fillContainer' && styles.fillContainer,
        type === 'fullScreen' && styles.fullScreen,
        type === 'fullWidth' && styles.fullWidth,
        overlay && styles.overlay,
      )}
      id={id}
      style={desktopMaxHeight ? { '--desktopMaxHeight': `${desktopMaxHeight}vw` } as CSSProperties : undefined}
    >
      {link && (
        <ImageWrapLink
          link={link}
          style={{
            ...(type === 'fillContainer'
              ? {
                width: '100%',
                height: '100%',
              }
              : {} as CSSProperties),
            ...(type === 'fullScreen'
              ? {
                height: desktopMaxHeight ? `${desktopMaxHeight}vw` : 'auto',
                maxHeight: desktopMaxHeight ? `${desktopMaxHeight}vw` : 'fit-content',
              }
              : {} as CSSProperties),
          }}
          {...props}
        >
          {picture}
        </ImageWrapLink>
      )}

      {!link && <div className={styles.imageWrapper}>{picture}</div>}

      {(products.length !== 0 || caption) && type !== 'fillContainer' && (
        <ProductsAndCaption caption={caption} products={products} />
      )}
    </div>
  )
}
